import { api } from '../Components/functions/Apiwithouttoken';

/**
 * IcalSyncService - Handles all iCal sync operations
 * 
 * This service provides methods to:
 * - Add/remove iCal URLs to properties
 * - Get sync status
 * - Trigger manual syncs
 * - Validate iCal URLs
 * - Update sync settings
 */
class IcalSyncService {
  /**
   * Add iCal URL to a property
   * @param {string} propertyId - Property token_id
   * @param {string} icalUrl - iCal export URL
   * @param {string} platform - 'airbnb', 'booking', 'vrbo', 'other'
   * @returns {Promise<Object>} Response with sync results
   */
  async addIcalUrl(propertyId, icalUrl, platform) {
    try {
      const response = await api('property/addIcalUrl', {
        propertyId,
        icalUrl,
        platform
      });
      return response;
    } catch (error) {
      console.error('Error adding iCal URL:', error);
      throw new Error(error.message || 'Failed to add iCal URL');
    }
  }

  /**
   * Remove iCal URL from a property
   * @param {string} propertyId - Property token_id
   * @param {string} icalUrlId - iCal URL MongoDB _id
   * @returns {Promise<Object>} Success response
   */
  async removeIcalUrl(propertyId, icalUrlId) {
    try {
      const response = await api('property/removeIcalUrl', {
        propertyId,
        icalUrlId
      });
      return response;
    } catch (error) {
      console.error('Error removing iCal URL:', error);
      throw new Error(error.message || 'Failed to remove iCal URL');
    }
  }

  /**
   * Get iCal sync status for a property
   * @param {string} propertyId - Property token_id
   * @returns {Promise<Object>} Sync status with icalUrls and blocked dates
   */
  async getIcalSyncStatus(propertyId) {
    try {
      const response = await api('property/getIcalSyncStatus', {
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error getting sync status:', error);
      throw new Error(error.message || 'Failed to get sync status');
    }
  }

  /**
   * Manually trigger sync for a property
   * @param {string} propertyId - Property token_id
   * @param {string} icalUrlId - Optional: sync only this specific URL
   * @returns {Promise<Object>} Sync results with blocked dates count
   */
  async triggerManualSync(propertyId, icalUrlId = null) {
    try {
      const payload = { propertyId };
      if (icalUrlId) {
        payload.icalUrlId = icalUrlId;
      }
      
      const response = await api('property/syncIcalNow', payload);
      return response;
    } catch (error) {
      console.error('Error triggering sync:', error);
      throw new Error(error.message || 'Failed to sync calendar');
    }
  }

  /**
   * Get all blocked dates (internal + external)
   * @param {string} propertyId - Property token_id
   * @returns {Promise<Object>} Merged blocked dates from all sources
   */
  async getAllBlockedDates(propertyId) {
    try {
      const response = await api('property/getAllBlockedDates', {
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error getting blocked dates:', error);
      throw new Error(error.message || 'Failed to get blocked dates');
    }
  }

  /**
   * Update sync settings for a property
   * @param {string} propertyId - Property token_id
   * @param {Object} syncSettings - Settings object with autoSync, syncFrequency, enabled
   * @returns {Promise<Object>} Success response
   */
  async updateSyncSettings(propertyId, syncSettings) {
    try {
      const response = await api('property/updateIcalSyncSettings', {
        propertyId,
        syncSettings
      });
      return response;
    } catch (error) {
      console.error('Error updating sync settings:', error);
      throw new Error(error.message || 'Failed to update settings');
    }
  }

  /**
   * Validate iCal URL before adding
   * @param {string} icalUrl - iCal URL to validate
   * @returns {Promise<Object>} Validation result with valid flag and event count
   */
  async validateIcalUrl(icalUrl) {
    try {
      const response = await api('property/validateIcalUrl', {
        icalUrl
      });
      return response;
    } catch (error) {
      console.error('Error validating iCal URL:', error);
      throw new Error(error.message || 'Failed to validate URL');
    }
  }

  /**
   * Get iCal export URL for a property
   * @param {string} propertyId - Property token_id
   * @returns {Promise<Object>} Response with export URL and statistics
   */
  async getIcalExportUrl(propertyId) {
    try {
      const response = await api('property/getIcalExportUrl', {
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error getting export URL:', error);
      throw new Error(error.message || 'Failed to get export URL');
    }
  }

  /**
   * Download iCal file for a property
   * @param {string} propertyId - Property token_id
   * @returns {Promise<Object>} Success response
   */
  async downloadIcalFile(propertyId) {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_SERVER}property/exportIcal/${propertyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `codedestate-bookings-${propertyId}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'iCal file downloaded successfully' };
    } catch (error) {
      console.error('Error downloading iCal file:', error);
      throw new Error(error.message || 'Failed to download iCal file');
    }
  }

  /**
   * Get export statistics for a property
   * @param {string} propertyId - Property token_id
   * @returns {Promise<Object>} Export statistics
   */
  async getExportStats(propertyId) {
    try {
      const response = await api('property/getIcalExportUrl', {
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error getting export stats:', error);
      throw new Error(error.message || 'Failed to get export stats');
    }
  }

  /**
   * Get scraped blocked dates for verification (without saving to DB)
   * @param {string} listingUrl - Listing URL to scrape
   * @param {string} propertyId - Optional: Property token_id
   * @returns {Promise<Object>} Scraped blocked dates for verification
   */
  async getScrapedBlockedDates(listingUrl, propertyId = null) {
    try {
      const response = await api('property/getScrapedBlockedDates', {
        listingUrl,
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error getting scraped blocked dates:', error);
      throw new Error(error.message || 'Failed to get scraped blocked dates');
    }
  }

  // ============================================================================
  // URL SCRAPING METHODS - New functionality for listing URL sync
  // ============================================================================

  /**
   * Validate listing URL before adding
   * @param {string} listingUrl - Listing URL to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateListingUrl(listingUrl) {
    try {
      const response = await api('property/validateListingUrl', {
        listingUrl
      });
      return response;
    } catch (error) {
      console.error('Error validating listing URL:', error);
      throw new Error(error.message || 'Failed to validate listing URL');
    }
  }

  /**
   * Scrape blocked dates from a listing URL
   * @param {string} listingUrl - Listing URL to scrape
   * @param {string} propertyId - Optional: Property token_id to save dates
   * @returns {Promise<Object>} Scrape results with blocked dates
   */
  async scrapeListingCalendar(listingUrl, propertyId = null) {
    try {
      const response = await api('property/scrapeListingCalendar', {
        listingUrl,
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error scraping listing calendar:', error);
      throw new Error(error.message || 'Failed to scrape listing calendar');
    }
  }

  /**
   * Add listing URL to a property
   * @param {string} propertyId - Property token_id
   * @param {string} listingUrl - Listing URL (Airbnb, Booking.com, Vrbo)
   * @param {string} platform - 'airbnb', 'booking', 'vrbo'
   * @returns {Promise<Object>} Response with sync results
   */
  async addListingUrl(propertyId, listingUrl, platform) {
    try {
      const response = await api('property/addListingUrl', {
        propertyId,
        listingUrl,
        platform
      });
      return response;
    } catch (error) {
      console.error('Error adding listing URL:', error);
      throw new Error(error.message || 'Failed to add listing URL');
    }
  }

  /**
   * Remove listing URL from a property
   * @param {string} propertyId - Property token_id
   * @param {string} listingUrlId - Listing URL MongoDB _id
   * @returns {Promise<Object>} Success response
   */
  async removeListingUrl(propertyId, listingUrlId) {
    try {
      const response = await api('property/removeListingUrl', {
        propertyId,
        listingUrlId
      });
      return response;
    } catch (error) {
      console.error('Error removing listing URL:', error);
      throw new Error(error.message || 'Failed to remove listing URL');
    }
  }

  /**
   * Get listing URLs for a property
   * @param {string} propertyId - Property token_id
   * @returns {Promise<Object>} Response with listing URLs
   */
  async getListingUrls(propertyId) {
    try {
      const response = await api('property/getListingUrls', {
        propertyId
      });
      return response;
    } catch (error) {
      console.error('Error getting listing URLs:', error);
      throw new Error(error.message || 'Failed to get listing URLs');
    }
  }

  /**
   * Manually trigger sync for a listing URL
   * @param {string} propertyId - Property token_id
   * @param {string} listingUrlId - Listing URL MongoDB _id
   * @returns {Promise<Object>} Sync results with blocked dates count
   */
  async syncListingUrl(propertyId, listingUrlId) {
    try {
      const response = await api('property/syncListingUrl', {
        propertyId,
        listingUrlId
      });
      return response;
    } catch (error) {
      console.error('Error syncing listing URL:', error);
      throw new Error(error.message || 'Failed to sync listing URL');
    }
  }

  /**
   * Get scraping logs for a property
   * @param {string} propertyId - Property token_id
   * @param {number} limit - Number of logs to return
   * @returns {Promise<Object>} Response with scraping logs
   */
  async getScrapingLogs(propertyId, limit = 20) {
    try {
      const response = await api('property/getScrapingLogs', {
        propertyId,
        limit
      });
      return response;
    } catch (error) {
      console.error('Error getting scraping logs:', error);
      throw new Error(error.message || 'Failed to get scraping logs');
    }
  }

  /**
   * Get scraping statistics for a property
   * @param {string} propertyId - Property token_id
   * @param {number} days - Number of days to include in stats
   * @returns {Promise<Object>} Response with scraping statistics
   */
  async getScrapingStats(propertyId, days = 30) {
    try {
      const response = await api('property/getScrapingStats', {
        propertyId,
        days
      });
      return response;
    } catch (error) {
      console.error('Error getting scraping stats:', error);
      throw new Error(error.message || 'Failed to get scraping stats');
    }
  }

  /**
   * Detect platform from URL
   * @param {string} url - Listing URL
   * @returns {string|null} Platform type or null
   */
  detectPlatform(url) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      if (hostname.includes('airbnb.com')) {
        return 'airbnb';
      } else if (hostname.includes('booking.com')) {
        return 'booking';
      } else if (hostname.includes('vrbo.com') || hostname.includes('homeaway.com')) {
        return 'vrbo';
      }

      return null;
    } catch (error) {
      console.error('Error detecting platform:', error);
      return null;
    }
  }

  /**
   * Validate URL format for platform
   * @param {string} url - Listing URL
   * @param {string} platform - Platform type
   * @returns {boolean} Valid URL
   */
  validateUrlFormat(url, platform) {
    try {
      const urlObj = new URL(url);
      
      switch (platform) {
        case 'airbnb':
          return (urlObj.hostname.includes('airbnb') || 
                  urlObj.hostname.includes('airbnb.co.in') || 
                  urlObj.hostname.includes('airbnb.co.uk')) && 
                 (urlObj.pathname.includes('/rooms/') || urlObj.pathname.includes('/plus/'));
        case 'booking':
          return urlObj.hostname.includes('booking') && 
                 (urlObj.pathname.includes('/hotel/') || urlObj.pathname.includes('/property/'));
        case 'vrbo':
          return (urlObj.hostname.includes('vrbo') || 
                  urlObj.hostname.includes('homeaway.com')) &&
                 urlObj.pathname.length > 1;
        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
export default new IcalSyncService();





